import { useState, useEffect } from 'react';
import { track, EVENTS } from '@/lib/mixpanel';
import { useAuth } from '@/lib/auth-context';
import { handleCheckout } from "@/lib/checkout";
import { Button } from '@/components/ui/button';
import { IconArrowLeft, IconLoader, IconStar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getTestimonialsByLanguage } from '@/content/testimonials';
import { StepProgress } from '@/components/common/StepProgress';
import { Link, useNavigate } from 'react-router-dom';
import { getLanguagePath } from '@/lib/utils';
import { MetaHead } from "@/components/common/MetaHead";
import { GoogleSignInButton } from '@/components/common/GoogleSignInButton';

export default function SignIn() {
  const navigate = useNavigate();
  const { signInWithGoogleOAuth } = useAuth();
  const { t, i18n } = useTranslation();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [testimonial, setTestimonial] = useState('');
  const [gradient, setGradient] = useState('from-blue-400 to-purple-500');
  const planId = localStorage.getItem('pendingPurchasePlanId');

  useEffect(() => {
    // Track page view
    track(EVENTS.VIEW_SIGN_IN_PAGE, {
      authRedirectTo: localStorage.getItem('authRedirectTo')
    });

    // Set random testimonial
    const testimonials = getTestimonialsByLanguage(i18n.language);
    const randomTestimonialIndex = Math.floor(Math.random() * testimonials.length);
    const selected = testimonials[randomTestimonialIndex];
    if (selected) {
      setTestimonial(selected.content);
    }

    const gradients = [
      'from-blue-400 to-purple-500',
      'from-pink-500 to-orange-400',
      'from-green-400 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-yellow-400 to-orange-500',
      'from-blue-500 to-teal-400',
    ];
    const randomGradient = gradients[Math.floor(Math.random() * gradients.length)];
    if (randomGradient) {
      setGradient(randomGradient);
    }
  }, [i18n.language]);

  const handleGoogleButtonSignIn = async (user: any) => {
    console.log('Google button sign-in successful, user:', user);

    // Handle pending purchase if exists
    if (planId && user) {
      await handleCheckout({
        planId: planId,
        userId: user.id,
        navigate,
        setIsLoading: undefined, // No loading state needed here
        skipConfirmation: true,
      });
    } else {
      // No pending purchase, redirect to intended page
      const redirectTo = localStorage.getItem('authRedirectTo') || '/';
      localStorage.removeItem('authRedirectTo');
      navigate(getLanguagePath(redirectTo), { replace: true });
    }
  };

  const handleGoogleButtonError = (error: any) => {
    console.error('Google button sign-in error:', error);
    // Could show a toast notification here if needed
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      track(EVENTS.INITIATE_SIGN_IN, { provider: 'google' });

      // // Try One Tap first
      // try {
      //   const signedInUser = await signInWithGoogleOneTap();
      //   console.log('One Tap sign-in successful, user:', signedInUser);

      //   // Handle pending purchase if exists
      //   if (planId && signedInUser) {
      //     // localStorage.removeItem('pendingPurchasePlanId');
      //     await handleCheckout({
      //       planId: planId,
      //       userId: signedInUser.id,
      //       email: signedInUser.email,
      //       provider: signedInUser.app_metadata?.provider,
      //       promoCode: promoCode || "",
      //       navigate,
      //       setIsLoading: undefined, // No loading state needed here
      //       skipConfirmation: true,
      //     });
      //   } else {
      //     // No pending purchase, redirect to intended page
      //     const redirectTo = localStorage.getItem('authRedirectTo') || '/';
      //     localStorage.removeItem('authRedirectTo');
      //     navigate(getLanguagePath(redirectTo), { replace: true });
      //   }
      // } catch (oneTapError) {
      //   console.log('One Tap failed, falling back to OAuth redirect:', oneTapError);
      //   // Fallback to OAuth redirect
      //   await signInWithGoogleOAuth();
      //   // Note: OAuth redirect will navigate away from this page
      // }

      await signInWithGoogleOAuth();

    } catch (error) {
      console.error('Error signing in with Google:', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const TestimonialSection = () => (
    <div className="flex flex-col gap-3 max-w-xs">
      <p className="text-xs lg:text-sm leading-normal lg:leading-normal text-slate-500 lg:text-slate-200">
        {testimonial}
      </p>
      <div className="flex gap-1 mb-3">
        <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
        <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
        <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
        <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
        <IconStar className="w-3 h-3 fill-yellow-300 text-yellow-300" />
      </div>
      <div className="flex items-center gap-2">
        <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${gradient}`} />
        <p className="text-xs lg:text-sm leading-normal lg:leading-normal text-slate-400">
          {t('auth.appStoreReview')}
        </p>
      </div>
    </div>
  );

  function SignInContent() {
    return (
      <div className="w-full max-w-sm space-y-4">
        <div className="flex flex-col items-center space-y-4 text-center pt-12">
          <h1 className="text-base lg:text-xl font-semibold">
            {t('auth.signIn')}
          </h1>
          {/* <p className="text-sm text-muted-foreground text-left px-3 max-w-xs">
            {t('auth.signInDescription')}{t('auth.signInHint')}
          </p> */}
        </div>
        <div className="space-y-2 items-center flex flex-col">
          {/* New Google Sign In Button */}
          <GoogleSignInButton
            onSuccess={handleGoogleButtonSignIn}
            onError={handleGoogleButtonError}
            className="w-full max-w-xs lg:max-w-none"
            buttonConfig={{
              size: 'large',
              width: 320,
              locale: i18n.language
            }}
          />

          {/* <Button
            variant="outline"
            className="w-full py-5 rounded-lg max-w-xs lg:max-w-none"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? (
              <IconLoader className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <img
                src="/brands/google.svg"
                alt="Google"
                className="w-5 h-5 mr-2"
              />
            )}
            {t('auth.signInWithGoogle')} (OAuth)
          </Button> */}

          {/* <Button
            variant="outline"
            className="w-full py-5 rounded-lg max-w-xs lg:max-w-none"
            onClick={handleAppleSignIn}
            disabled={isAppleLoading}
          >
            {isAppleLoading ? (
              <IconLoader className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <img
                src="/brands/apple.svg"
                alt="Apple"
                className="w-5 h-5 mr-2"
              />
            )}
            {t('auth.signInWithApple')}
          </Button> */}
        </div>

        <div className="space-y-6 pt-12 pb-4 max-w-xs mx-auto lg:max-w-none">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-200"></div>
            <h2 className="text-sm text-slate-600 px-3 text-center">
              {t('auth.signInWhy')}
            </h2>
            <div className="h-px flex-1 bg-slate-200"></div>
          </div>

          <p className="max-w-md mx-auto text-sm text-slate-600 text-left px-3">
            {t('auth.signInHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-12">
      <MetaHead
        title={t('meta.signIn.title')}
        description={t('meta.signIn.description')}
      />
      {/* Left Section - Desktop */}
      <div className="hidden lg:flex flex-col lg:col-span-5 bg-slate-950 px-12">
        <div className="p-6 lg:p-10">
          <Link to={getLanguagePath('')}>
            <img
              src="/brands/clarymind-logo_logotype-app-transparent-dark-outline.svg"
              alt="Clarymind"
              className="h-12 w-auto"
            />
          </Link>
        </div>
        <div className="p-6 lg:p-10 mt-auto">
          <TestimonialSection />
        </div>
      </div>

      {/* Mobile Layout Elements */}
      <div className="lg:hidden flex flex-col min-h-screen">

        {/* <div className="lg:hidden items-center justify-center p-6">
          <StepProgress currentStep={1} />
        </div> */}

        {/* Back */}
        <div className="w-full max-w-md mx-auto text-center px-6 lg:px-4 pt-4 lg:pt-8 pb-2">
          <button
            onClick={() => navigate(getLanguagePath('/'))}
            className="px-4 py-2 text-slate-400 rounded-xl text-sm hover:text-slate-500 flex justify-start items-center gap-1 group transition-all duration-300"
          >
            <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
            {t('checkout.back')}
          </button>
        </div>

        {/* Spacer - Mobile */}
        <div className="flex-grow block lg:hidden" />

        {/* Logo - Mobile */}
        <div className="p-0 lg:p-6">
          <div className="flex items-center justify-center">
            <img
              src="/brands/clarymind-logo_logotype-app-transparent-dark-outline.svg"
              alt="Clarymind"
              className="h-12 w-auto hidden lg:block"
            />
            <img
              src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
              alt="Clarymind"
              className="h-12 w-auto block lg:hidden"
            />
          </div>
        </div>

        {/* Sign In - Mobile */}
        <div className="lg:flex-grow mx-auto lg:hidden items-center justify-center p-0 pb-24 lg:p-6">
          <SignInContent />
        </div>

        {/* Spacer - Mobile */}
        <div className="flex-grow block lg:hidden" />

        {/* Testimonial - Mobile */}
        {/* <div className="p-6">
          <div className="flex flex-col justify-center items-center">
            <div className="h-px w-full bg-slate-200 my-6 max-w-xs" />
            <TestimonialSection />
          </div>
        </div> */}

      </div>

      {/* Sign In - Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:col-span-7 items-center justify-center p-8">
        {/* <div className="w-full max-w-md">
          <StepProgress currentStep={1} />
        </div> */}
        {/* Back */}
        <div className="w-full max-w-md mx-auto text-center px-6 lg:px-4 pt-4 lg:pt-8 pb-2">
          <button
            onClick={() => navigate(getLanguagePath('/'))}
            className="px-4 py-2 text-slate-400 rounded-xl text-sm hover:text-slate-500 flex justify-start items-center gap-1 group transition-all duration-300"
          >
            <IconArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-all duration-300" />
            {t('checkout.back')}
          </button>
        </div>
        <div className="flex-grow"></div>
        <div className="pb-24">
          <SignInContent />
        </div>
        <div className="flex-grow"></div>
      </div>
    </div>
  );
}